import {
  addToast,
  Button,
  DateInput,
  Icon,
  Modal,
  Select,
  TextInput,
} from '@octano/global-ui';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { useValidations } from '../../../../hooks/useValidations';
import { useForm } from 'react-hook-form';
import { AdmissionScope, Offer, OfferListFormValues, Process } from '../types';
import { SectionTitle } from '../../../../components/text';
import {
  formatFirstInstallmentRule,
  getFirstInstallmentRule,
  getSelectedAdmissionScope,
} from '../utils';
import useFirstInstallmentRule from '../hooks/useFirstInstallmentRule';
import { useProcessList } from './ProcessListLoader';
import { updateAdmission } from '../api';
import Tooltip from '../../../../components/tooltip/Tooltip';

interface Props {
  toggle: (offer?: any) => void;
  offer: Offer;
  process: Process;
}

const DEFAULT_VALUES = {
  startDate: null,
  duration: null,
  enrollmentType: null,
  maxInstallments: null,
  firstExpirationRule: null,
  selectDay: null,
  selectMonth: null,
  selectDate: null,
};

export default function OffersListEditModal({ toggle, offer, process }: Props) {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_ACADEMIC_OFFER);

  const { msgValidations } = useValidations();
  const { refresh } = useProcessList();

  const texts = useMemo(() => {
    const prefix = 'offersList.modalForm';
    return {
      title: t(`${prefix}.title`),
      startDate: t(`${prefix}.startDate`),
      duration: t(`${prefix}.duration`),
      enrollmentType: t(`${prefix}.enrollmentType`),
      maxInstallments: t(`${prefix}.maxInstallments`),
      maxInstallmentsTooltip: t(`${prefix}.maxInstallmentsTooltip`),
      firstExpirationRule: t(`${prefix}.firstExpirationRule`),
      date: t(`${prefix}.date`),
      firstExpirationRuleTitle: t(`${prefix}.firstExpirationRuleTitle`),
      firstExpirationRuleSubTitle: t(`${prefix}.firstExpirationRuleSubTitle`),
      save: t(`${prefix}.save`),
      close: t(`${prefix}.close`),
    };
  }, [t]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<OfferListFormValues>({
    defaultValues: DEFAULT_VALUES,
  });

  const firstInstallmentRule = watch('firstInstallmentRule');

  const toggleModal = () => {
    reset(DEFAULT_VALUES);
    toggle();
  };

  const handleClose = useCallback(() => {
    reset(DEFAULT_VALUES);
    toggle(undefined);
  }, [reset, toggle]);

  const onSubmit = useCallback(
    async (data: OfferListFormValues) => {
      const { startDate, duration, enrollmentType, maxInstallments, ...rest } =
        data;

      const firstInstallmentRuleValue = formatFirstInstallmentRule(
        rest,
        offer.firstInstallmentRule?.id,
      );

      if (!firstInstallmentRuleValue) return;

      const response = await updateAdmission({
        admissionId: offer.admissionId,
        data: {
          startDate,
          duration,
          admissionScopes: enrollmentType?.value
            ? (enrollmentType.value.toString().split(',') as AdmissionScope[])
            : null,
          maxInstallments: maxInstallments?.value
            ? +maxInstallments.value
            : null,
          firstInstallmentRuleValue,
          tuitionProcessFirstInstallmentRuleId:
            process.firstInstallmentRule?.id ?? undefined,
        },
      });

      if (response.status === 200) {
        addToast({
          text: t('processList.modalForm.success'),
          color: 'success',
        });
      } else {
        addToast({
          text: t('processList.modalForm.error'),
          color: 'danger',
        });
      }
      refresh();
      handleClose();
    },
    [
      t,
      offer.admissionId,
      refresh,
      handleClose,
      offer.firstInstallmentRule?.id,
      process.firstInstallmentRule?.id,
    ],
  );

  const { options, formFields } = useFirstInstallmentRule({
    key: firstInstallmentRule?.value as string,
    control,
  });

  useEffect(() => {
    if (offer.firstInstallmentRule) {
      const {
        key,
        selectDate,
        selectMonth,
        selectYear,
        selectDay,
        validityRange,
      } = getFirstInstallmentRule(offer.firstInstallmentRule);

      const selectedOption = options.find((option) => option.value === key);

      return reset({
        startDate: offer?.startDate ?? null,
        duration: offer?.duration ?? null,
        enrollmentType: getSelectedAdmissionScope(offer?.admissionScopes ?? []),
        maxInstallments: offer?.maxInstallments
          ? {
              label: offer?.maxInstallments?.toString(),
              value: offer?.maxInstallments,
            }
          : null,
        firstInstallmentRule: {
          label: selectedOption?.label ?? '',
          value: selectedOption?.value ?? '',
        },
        selectDate: selectDate ?? null,
        selectMonth: selectMonth ?? null,
        selectYear: selectYear ?? null,
        selectDay: selectDay ?? null,
        validityRange: validityRange?.toString() ?? null,
      });
    }

    return reset({
      startDate: offer?.startDate ?? null,
      duration: offer?.duration ?? null,
      enrollmentType: getSelectedAdmissionScope(offer?.admissionScopes ?? []),
      maxInstallments: offer?.maxInstallments?.toString()
        ? {
            label: offer?.maxInstallments?.toString(),
            value: offer?.maxInstallments,
          }
        : null,
    });
  }, [offer, setValue, reset, options]);

  return (
    <Modal isOpen toggle={toggle} size="lg" closeOnBackdrop={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12}>
            <h3 className="fs-22 text-center mb-3">{texts.title}</h3>
          </Col>
          <Col xs={12} md={6} className="mt-3">
            <DateInput
              label={texts.startDate}
              control={control}
              name="startDate"
            />
          </Col>
          <Col xs={12} md={6} className="mt-3">
            <TextInput
              label={texts.duration}
              control={control}
              name="duration"
            />
          </Col>
          <Col xs={12} className="mt-3">
            <Select
              label={texts.enrollmentType}
              control={control}
              name="enrollmentType"
              options={[
                {
                  label: 'Matrícula presencial',
                  value: AdmissionScope.OS,
                },
                {
                  label: 'Matrícula autoatendida (online)',
                  value: AdmissionScope.OL,
                },
                {
                  label: 'Matrícula presencial y autoatendida',
                  value: `${AdmissionScope.OS},${AdmissionScope.OL}`,
                },
              ]}
            />
          </Col>
          <Col xs={12} className="mt-3">
            <Row xs={12}>
              <Col xs={11}>
                <Select
                  label={texts.maxInstallments}
                  control={control}
                  name="maxInstallments"
                  options={Array.from({ length: 12 }, (_, i) => ({
                    label: (i + 1).toString(),
                    value: (i + 1).toString(),
                  }))}
                />
              </Col>
              <Col xs={1} className="d-flex align-items-center">
                <Tooltip
                  id="info_tooltip_max_installments"
                  placement="bottom"
                  text={texts.maxInstallmentsTooltip}
                >
                  <Icon name="information_input" className="mb-3" />
                </Tooltip>
              </Col>
            </Row>
          </Col>
          <Col xs={12} className="mt-3">
            <SectionTitle text={texts.firstExpirationRuleTitle} />
            <p className="fs-14 mb-3">{texts.firstExpirationRuleSubTitle}</p>
          </Col>
          <Col xs={12} className="mt-2">
            <Select
              label={texts.firstExpirationRule}
              name="firstInstallmentRule"
              options={options}
              control={control}
              placeholder={texts.firstExpirationRule}
              rules={{ required: msgValidations.required }}
            />
          </Col>
          {firstInstallmentRule && <Col xs={12}>{formFields}</Col>}
        </Row>
        <Row className="mt-5">
          <Col xs={6}>
            <Button
              text={texts.close}
              outlined
              onClick={toggleModal}
              fullwidth
              disabled={isSubmitting}
            />
          </Col>
          <Col xs={6}>
            <Button
              text={texts.save}
              type="submit"
              fullwidth
              loading={isSubmitting}
            />
          </Col>
        </Row>
      </form>
    </Modal>
  );
}
